/**
 * Which plan is waiting — the one card both leftover prompts draw, so a trainer recognises the same
 * plan the same way whether they meet it on „+ Nowy program" or on a reloaded `/plans/nowy`.
 *
 * Name first, because it is the trainer's own word for the plan. Then what a trainer checks to tell
 * two half-built plans apart — its category and how much is in it — and how safe it is: when the
 * device last kept it, or that only this tab holds it. Every one of those is decided in
 * `summariseLeftoverPlan`; this component words and draws them.
 *
 * `id` is the card's, so a prompt can name it in `aria-describedby` and a screen reader hears WHICH
 * plan the question is about, not only the question.
 */

import { useTranslation } from 'react-i18next';

import { summariseLeftoverPlan, type LeftoverPlan } from '~/entities/plan-draft';
import { relativeTimeFormat } from '~/shared/lib/intlCache';

import styles from './LeftoverPlanCard.module.css';

interface LeftoverPlanCardProps {
  readonly plan: LeftoverPlan;
  readonly id: string;
}

export function LeftoverPlanCard({ plan, id }: LeftoverPlanCardProps) {
  const { t, i18n } = useTranslation();
  const summary = summariseLeftoverPlan(plan);

  const figures = [
    summary.category === null ? null : t(`plans.category.${summary.category}`),
    t('web:planResume.phases', { count: summary.phases }),
    t('web:planResume.trainings', { count: summary.trainings }),
    t('web:planResume.exercises', { count: summary.exercises }),
  ].filter((figure) => figure !== null);

  const freshness = summary.freshness;
  // `numeric: 'auto'` so a minute-old record reads „teraz" / "now" rather than „0 sekund temu".
  const when =
    freshness.kind === 'savedAt'
      ? t('web:planResume.savedAt', {
          when: relativeTimeFormat(i18n.language, { numeric: 'auto' }).format(
            freshness.age.value === 0 ? 0 : -freshness.age.value,
            freshness.age.unit,
          ),
        })
      : freshness.kind === 'tabOnly'
        ? t('web:planResume.tabOnly')
        : null;

  return (
    <div id={id} className={styles.card}>
      <img className={styles.cover} src={summary.coverUrl} alt="" />
      <div className={styles.body}>
        <p className={styles.name}>{summary.name}</p>
        {/* Nominative, as the create window's head echoes it: a surname's case is never guessed. */}
        {summary.clientName !== null && (
          <p className={styles.figures}>{t('web:planCreate.echo', { name: summary.clientName })}</p>
        )}
        <p className={styles.figures}>{figures.join(' · ')}</p>
        {when !== null && <p className={styles.when}>{when}</p>}
        {summary.saveMayHaveLanded && (
          <p className={styles.warning}>{t('web:planResume.maybeSaved')}</p>
        )}
      </div>
    </div>
  );
}
